import * as React from 'react'
import type { Label, Meal, MealWeek, NoteColor, PlannedMeal } from '@shared/types'
import { advanceMealWeeks, bumpMeal, countMealUses, MEAL_FIELDS, mealsForWeek, nextMealOrder, planMealReorder, PLANNED_MEAL_FIELDS, type PlannedMealPatch, type RemainingMealsChoice } from '@shared/meal-plan'
import type { LocalDatabase } from '@/data/local-database'
import { withoutCollectionMetadata } from '@/lib/collection-metadata'
import { useNotes } from '@/state/notes-context'

export type MealDetails = Pick<Meal, 'name' | 'ingredients' | 'recipeUrl' | 'labelIds'>

const MealsContext = React.createContext<ReturnType<typeof useMealPlanState> | null>(null)

export function MealsProvider({ children }: { children: React.ReactNode }) {
  const plan = useMealPlanState()
  return React.createElement(MealsContext.Provider, { value: plan }, children)
}

export function useMealPlan() {
  const plan = React.useContext(MealsContext)
  if (!plan) throw new Error('useMealPlan must be used inside MealsProvider')
  return plan
}

function useMealPlanState() {
  const { currentUser } = useNotes()
  const [meals, setMeals] = React.useState<Meal[]>([])
  const [mealLabels, setMealLabels] = React.useState<Label[]>([])
  const [entries, setEntries] = React.useState<PlannedMeal[]>([])
  const mealUseCounts = React.useMemo(() => countMealUses(entries), [entries])
  const [ready, setReady] = React.useState(false)
  const [error, setError] = React.useState('')
  const database = React.useRef<LocalDatabase | null>(null)
  const lastClock = React.useRef(0)

  React.useEffect(() => {
    let disposed = false
    let release: (() => void) | undefined
    const subscriptions: Array<{ unsubscribe(): void }> = []
    setReady(false); setError(''); setMeals([]); setMealLabels([]); setEntries([])
    void import('@/data/local-database').then(({ acquireLocalDatabase }) => {
      if (disposed) return null
      const lease = acquireLocalDatabase(currentUser.id)
      release = lease.release
      return lease.ready
    }).then((local) => {
      if (!local || disposed) return
      database.current = local
      const refreshMeals = () => setMeals(Array.from(local.meals.values()).filter((meal) => !meal.isDeleted).map(({ isDeleted: _, ...meal }) => withoutCollectionMetadata(meal)))
      const refreshEntries = () => setEntries(Array.from(local.plannedMeals.values()).filter((meal) => !meal.isDeleted).map(({ isDeleted: _, ...meal }) => withoutCollectionMetadata(meal)))
      const refreshLabels = () => setMealLabels(Array.from(local.labels.values()).filter((label) => !label.isDeleted && label.kind === 'meal').map(withoutCollectionMetadata).sort((a, b) => a.name.localeCompare(b.name)))
      subscriptions.push(local.labels.subscribeChanges(refreshLabels, { includeInitialState: true }))
      refreshLabels()
      subscriptions.push(local.meals.subscribeChanges(refreshMeals, { includeInitialState: true }), local.plannedMeals.subscribeChanges(refreshEntries, { includeInitialState: true }))
      refreshMeals(); refreshEntries(); setReady(true)
    }).catch((cause) => {
      console.error('Unable to open meal planning', cause)
      if (!disposed) setError('Meal planning could not be opened. Please reload and try again.')
    })
    return () => { disposed = true; subscriptions.forEach((subscription) => subscription.unsubscribe()); database.current = null; release?.() }
  }, [currentUser.id])

  const local = () => {
    if (!database.current) throw new Error('Meal planning is still loading. Please try again in a moment.')
    return database.current
  }
  const clock = (previous: Record<string, string> = {}) => {
    const observed = Math.max(0, ...Object.values(previous).map((value) => Number(value.split(':')[0]) || 0))
    lastClock.current = Math.max(Date.now(), lastClock.current + 1, observed + 1)
    return `${String(lastClock.current).padStart(13, '0')}:${currentUser.id}`
  }
  const allEntries = () => Array.from(local().plannedMeals.values()).filter((entry) => !entry.isDeleted)

  const addEntry = async (mealId: string, week: MealWeek) => {
    const timestamp = new Date().toISOString()
    const stamp = clock()
    const entry: PlannedMeal = {
      id: crypto.randomUUID(), mealId, week, sortOrder: nextMealOrder(allEntries(), week), shopped: false, completedAt: null,
      createdAt: timestamp, updatedAt: timestamp, fieldClock: Object.fromEntries(PLANNED_MEAL_FIELDS.map((field) => [field, stamp])),
    }
    await local().upsertPlannedMeal(entry)
    return entry
  }

  const createMeal = async (details: MealDetails, week: MealWeek) => {
    const timestamp = new Date().toISOString()
    const stamp = clock()
    const meal: Meal = { ...details, id: crypto.randomUUID(), createdAt: timestamp, updatedAt: timestamp, fieldClock: Object.fromEntries(MEAL_FIELDS.map((field) => [field, stamp])) }
    await local().upsertMeal(meal)
    await addEntry(meal.id, week)
  }

  const updateMeal = async (id: string, details: MealDetails, original?: MealDetails) => {
    const existing = local().meals.get(id)
    if (!existing) throw new Error('This meal is no longer available.')
    const changed = MEAL_FIELDS.filter((field) => JSON.stringify((original ?? existing)[field]) !== JSON.stringify(details[field]))
    if (!changed.length) return
    const stamp = clock(existing.fieldClock)
    const fieldClock = { ...existing.fieldClock, ...Object.fromEntries(changed.map((field) => [field, stamp])) }
    const patch = Object.fromEntries(changed.map((field) => [field, details[field]]))
    await local().upsertMeal({ ...existing, ...patch, fieldClock, updatedAt: new Date().toISOString() })
  }

  const updateEntry = async (id: string, patch: PlannedMealPatch) => {
    const existing = local().plannedMeals.get(id)
    if (!existing) throw new Error('This planned meal is no longer available.')
    const stamp = clock(existing.fieldClock)
    const fieldClock = { ...existing.fieldClock, ...Object.fromEntries(Object.keys(patch).map((field) => [field, stamp])) }
    await local().upsertPlannedMeal({ ...existing, ...patch, fieldClock, updatedAt: new Date().toISOString() })
  }

  const updateEntries = async (snapshot: PlannedMeal[], changes: Array<{ id: string; patch: PlannedMealPatch }>) => {
    const entriesById = new Map(snapshot.map((entry) => [entry.id, entry]))
    await local().updatePlannedMeals(changes.map(({ id, patch }) => {
      const entry = entriesById.get(id)!
      const stamp = clock(entry.fieldClock)
      return {
        ...entry, ...patch, updatedAt: new Date().toISOString(),
        fieldClock: { ...entry.fieldClock, ...Object.fromEntries(Object.keys(patch).map((field) => [field, stamp])) },
      }
    }))
  }

  return {
    meals, mealLabels, entries, mealUseCounts, ready, error, addEntry, createMeal, updateMeal,
    saveMealLabel: async (id: string | null, name: string, color: NoteColor) => {
      const trimmed = name.trim()
      if (!trimmed || trimmed.length > 80) throw new Error('Use a label name of 1–80 characters.')
      const labels = Array.from(local().labels.values()).filter((label) => !label.isDeleted && label.kind === 'meal')
      if (labels.some((label) => label.id !== id && label.name.toLocaleLowerCase() === trimmed.toLocaleLowerCase())) throw new Error('A meal label with that name already exists.')
      const label: Label = { id: id ?? crypto.randomUUID(), kind: 'meal', name: trimmed, color, updatedAt: new Date().toISOString() }
      local().upsertLabel(label)
      return label
    },
    removeMealLabel: async (id: string) => {
      for (const meal of Array.from(local().meals.values()).filter((meal) => !meal.isDeleted && meal.labelIds?.includes(id))) {
        await updateMeal(meal.id, { ...meal, labelIds: meal.labelIds.filter((labelId) => labelId !== id) })
      }
      local().deleteLabel(id)
    },
    advanceWeeks: async (remaining?: RemainingMealsChoice) => {
      const snapshot = allEntries()
      // Recheck persisted state when the action runs, including newly synced meals.
      if (!remaining && mealsForWeek(snapshot, 0).length) return false
      const changes = advanceMealWeeks(snapshot, remaining ?? 'keep', new Date().toISOString())
      await updateEntries(snapshot, changes)
      return true
    },
    reorderEntry: async (id: string, week: MealWeek, beforeId: string | null, afterId: string | null) => {
      const snapshot = allEntries()
      await updateEntries(snapshot, planMealReorder(snapshot, id, week, beforeId, afterId))
    },
    setShopped: (id: string, shopped: boolean) => updateEntry(id, { shopped }),
    bumpEntry: async (id: string, direction: 1 | -1 = 1) => {
      const entry = local().plannedMeals.get(id)
      if (!entry) return
      const patch = bumpMeal(entry, allEntries(), direction)
      if (patch) await updateEntry(id, patch)
    },
    completeEntry: async (id: string) => {
      const entry = local().plannedMeals.get(id)
      if (!entry) throw new Error('This planned meal is no longer available.')
      const snapshot = withoutCollectionMetadata({ ...entry })
      await updateEntry(id, { completedAt: new Date().toISOString() })
      return snapshot
    },
    restoreEntry: (entry: PlannedMeal) => updateEntry(entry.id, { completedAt: null, week: entry.week, sortOrder: entry.sortOrder, shopped: entry.shopped }),
  }
}
